import { useState } from 'react'
import { YStack, XStack, Text, styled, GetProps } from 'tamagui'

const AccordionContainer = styled(YStack, {
  name: 'AccordionContainer',
  gap: '$3',
})

const AccordionItemContainer = styled(YStack, {
  name: 'AccordionItem',
  backgroundColor: '$cardBackground',
  borderRadius: '$4',
  borderWidth: 1,
  borderColor: '$border',
  overflow: 'hidden',
  animation: 'medium',

  hoverStyle: {
    borderColor: '$orangePrimary',
  },
})

const AccordionHeader = styled(XStack, {
  name: 'AccordionHeader',
  padding: '$5',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'space-between',
  userSelect: 'none',

  hoverStyle: {
    backgroundColor: '$backgroundSecondary',
  },
})

const AccordionContent = styled(YStack, {
  name: 'AccordionContent',
  paddingHorizontal: '$5',
  paddingBottom: '$5',
  paddingTop: 0,
})

const ChevronIcon = styled(Text, {
  name: 'ChevronIcon',
  fontSize: '$5',
  color: '$orangePrimary',
  animation: 'accordion',

  variants: {
    open: {
      true: {
        transform: [{ rotate: '180deg' }],
      },
      false: {
        transform: [{ rotate: '0deg' }],
      },
    },
  } as const,
})

interface AccordionItemProps {
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
}

function AccordionItem({ question, answer, isOpen, onToggle }: AccordionItemProps) {
  return (
    <AccordionItemContainer>
      <AccordionHeader onPress={onToggle}>
        <Text
          fontFamily="$body"
          fontSize="$5"
          fontWeight="500"
          color="$textPrimary"
          flex={1}
          paddingRight="$4"
        >
          {question}
        </Text>
        <ChevronIcon open={isOpen}>
          {isOpen ? '−' : '+'}
        </ChevronIcon>
      </AccordionHeader>
      {isOpen && (
        <AccordionContent animation="accordion">
          <Text
            fontFamily="$body"
            fontSize="$4"
            lineHeight="$5"
            color="$textSecondary"
          >
            {answer}
          </Text>
        </AccordionContent>
      )}
    </AccordionItemContainer>
  )
}

interface AccordionProps {
  items: Array<{ question: string; answer: string }>
  allowMultiple?: boolean
}

export function Accordion({ items, allowMultiple = false }: AccordionProps) {
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set())

  const handleToggle = (index: number) => {
    setOpenIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        if (!allowMultiple) {
          next.clear()
        }
        next.add(index)
      }
      return next
    })
  }

  return (
    <AccordionContainer>
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          question={item.question}
          answer={item.answer}
          isOpen={openIndices.has(index)}
          onToggle={() => handleToggle(index)}
        />
      ))}
    </AccordionContainer>
  )
}

export type AccordionContainerProps = GetProps<typeof AccordionContainer>
